/* Crucial Tracking — keeps the checkout buttons in step with the IMEI gate
   after an Ajax cart render (two-tier plans, September 2026).

   The gate itself is Liquid: snippets/ct-cart-imei-check.liquid decides
   whether any self-serve plan line is missing a valid 15-digit "Device
   IMEI" property (or carries a quantity above one), sections/main-cart-
   footer.liquid renders the cart page's checkout button disabled on that
   flag, and sections/main-cart-items.liquid / sections/cart-notification-
   product.liquid render the per-line notices (ct-cart-imei-line) plus the
   cart-level markers. With JavaScript off the page reloads on every cart
   change, so Liquid alone holds.

   With JavaScript on, Dawn re-renders main-cart-items' .js-contents (and
   the footer's subtotal block, not its buttons) through the Section
   Rendering API, and the cart notification's product host is rebuilt on
   every add (Dawn's renderContents, or ct-cart-lines.js after a kit add).
   This script watches those two hosts with a MutationObserver — no
   dependency on who rendered or in what order — and mirrors the markers
   onto the buttons:
   - cart page: any [data-ct-imei-block] inside #main-cart-items → #checkout
     disabled, the footer note shown, the dynamic checkout buttons hidden;
   - notification: any [data-ct-cart-imei-blocked] / [data-ct-imei-block]
     inside #cart-notification-product → the notification's checkout button
     disabled and its note shown. The cart-level marker is rendered inside
     EVERY line of the notification section whenever the cart is blocked,
     because Dawn injects only the added line's inner HTML — so a tracker
     added on top of a bad plan line still finds the marker.

   The fitted add-on gate (October 2026, CT-A08 "batteries optional":
   snippets/ct-cart-addon-check.liquid / ct-cart-addon-line.liquid) uses the
   same arrangement with its own markers, whose values are the reasons it
   holds: 'unfitted' (a battery set not fitted to a tracker that takes it)
   and 'over' (more sets than trackers) — [data-ct-addon-block~=reason],
   [data-ct-cart-addon-blocked~=reason]. The two reasons share the cart
   page's footer note ([data-ct-addon-footer-note]) and each has its own
   notification note ([data-ct-addon-notification-note=reason]). Every gate
   shares each checkout button: it is disabled while ANY holds, a note
   shows while any gate using it holds, and aria-describedby lists exactly
   the shown notes.

   Loaded deferred from layout/theme.liquid on every page (the notification
   is on every page); idle until a host exists. */

(function () {
  'use strict';

  // One entry per gate. line: the per-line / banner marker inside the cart
  // page's items; cart: the cart-level marker the notification section
  // renders in every line; footerNote / notificationNote: that gate's notes.
  var GATES = [
    {
      line: '[data-ct-imei-block]',
      cart: '[data-ct-cart-imei-blocked]',
      footerNote: '[data-ct-imei-footer-note]',
      notificationNote: '[data-ct-imei-notification-note]',
    },
    {
      line: '[data-ct-addon-block~="unfitted"]',
      cart: '[data-ct-cart-addon-blocked~="unfitted"]',
      footerNote: '[data-ct-addon-footer-note]',
      notificationNote: '[data-ct-addon-notification-note="unfitted"]',
    },
    {
      line: '[data-ct-addon-block~="over"]',
      cart: '[data-ct-cart-addon-blocked~="over"]',
      footerNote: '[data-ct-addon-footer-note]',
      notificationNote: '[data-ct-addon-notification-note="over"]',
    },
  ];

  // states: [{ blocked, note }] — one per gate; gates may share a note. The
  // button is disabled while any gate holds; a note shows while any gate
  // using it holds; and aria-describedby carries exactly the shown notes'
  // ids (any other ids on it are left alone).
  function setBlocked(button, states) {
    if (!button) return;
    var notes = [];
    states.forEach(function (state) {
      if (!state.note) return;
      var entry = notes.filter(function (n) {
        return n.note === state.note;
      })[0];
      if (!entry) {
        entry = { note: state.note, blocked: false };
        notes.push(entry);
      }
      entry.blocked = entry.blocked || state.blocked;
    });
    var ours = [];
    var held = [];
    notes.forEach(function (entry) {
      entry.note.hidden = !entry.blocked;
      if (entry.note.id) {
        ours.push(entry.note.id);
        if (entry.blocked) held.push(entry.note.id);
      }
    });
    var blocked = states.some(function (state) {
      return state.blocked;
    });
    if (blocked) {
      button.setAttribute('disabled', 'disabled');
    } else {
      button.removeAttribute('disabled');
    }
    var others = (button.getAttribute('aria-describedby') || '').split(/\s+/).filter(function (id) {
      return id && ours.indexOf(id) === -1;
    });
    var describedBy = others.concat(held).join(' ');
    if (describedBy) {
      button.setAttribute('aria-describedby', describedBy);
    } else {
      button.removeAttribute('aria-describedby');
    }
  }

  function syncCartPage() {
    var items = document.getElementById('main-cart-items');
    var checkout = document.getElementById('checkout');
    if (!items || !checkout) return;
    var footer = document.getElementById('main-cart-footer');
    var empty = Boolean(footer && footer.classList.contains('is-empty'));
    var states = GATES.map(function (gate) {
      return { blocked: Boolean(items.querySelector(gate.line)), note: document.querySelector(gate.footerNote) };
    });
    // The notes and their aria-describedby belong to the gates alone; an
    // empty cart keeps Dawn's own disabled button (the footer is hidden by CSS).
    setBlocked(checkout, states);
    if (empty) checkout.setAttribute('disabled', 'disabled');
    var dynamic = document.querySelector('[data-ct-imei-dynamic]');
    if (dynamic) {
      dynamic.hidden = states.some(function (state) {
        return state.blocked;
      });
    }
  }

  function syncNotification() {
    var host = document.getElementById('cart-notification-product');
    var checkout = document.querySelector('[data-ct-imei-notification-checkout]');
    if (!host || !checkout) return;
    var states = GATES.map(function (gate) {
      return {
        blocked: Boolean(host.querySelector(gate.cart + ', ' + gate.line)),
        note: document.querySelector(gate.notificationNote),
      };
    });
    setBlocked(checkout, states);
  }

  var observed = typeof WeakSet === 'function' ? new WeakSet() : null;

  function watch(host, callback) {
    if (!host || !('MutationObserver' in window)) return;
    if (observed) {
      if (observed.has(host)) return;
      observed.add(host);
    }
    new MutationObserver(callback).observe(host, { childList: true, subtree: true });
    callback();
  }

  function init() {
    watch(document.getElementById('main-cart-items'), syncCartPage);
    watch(document.getElementById('cart-notification-product'), syncNotification);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
